import { useCallback, useRef, useState } from 'react'

interface UseMultiplePressOptions {
  onFinish?: () => void
  onCancel?: () => void
  threshold?: number // time window in ms for multiple presses (default: 1000ms)
}

export function useMultiplePress(
  callback: () => void,
  pressCount: number,
  options: UseMultiplePressOptions = {}
) {
  const { onFinish, onCancel, threshold = 1000 } = options

  const [currentPresses, setCurrentPresses] = useState(0)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const callbackRef = useRef(callback)

  // Update callback ref to always have the latest callback
  callbackRef.current = callback

  const reset = useCallback(() => {
    setCurrentPresses(0)
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  const handlePress = useCallback(() => {
    setCurrentPresses(prev => {
      const newCount = prev + 1

      // Clear existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      if (newCount >= pressCount) {
        // Target reached, trigger callback
        callbackRef.current()
        onFinish?.()
        reset()
        return 0
      } else {
        // Set timeout to reset if no more presses within threshold
        timeoutRef.current = setTimeout(() => {
          setCurrentPresses(0)
          onCancel?.()
        }, threshold)

        return newCount
      }
    })
  }, [pressCount, threshold, onFinish, onCancel, reset])

  return {
    onMouseDown: handlePress,
    onTouchStart: handlePress,
    currentPresses,
    reset
  }
}

